import { useState, useEffect } from 'react';
import axios from 'axios';
import { DMS_FILE_EXPORT, GRUNTWORX_INTEGRATION_STATUS } from 'constants/constants';
import { ExportStatus, MAX_POLL_ATTEMPTS, POLL_INTERVAL_PENDING, POLL_INTERVAL_PROCESSING, POLL_INTERVAL_FAILED } from './types';
import { getExportErrorMessage } from './utils';

interface UseGruntWorxExportReturn {
  isExporting: boolean;
  exportStatus: ExportStatus | null;
  pollAttempts: number;
  connectionStatus: string | null;
  handleExport: (documentIds: string[], sourceType: 'Request' | 'Document') => Promise<void>;
  resetExport: () => void;
  checkGruntworxConnection: () => Promise<void>;
}

export const useGruntWorxExport = (onComplete?: (status: ExportStatus) => void): UseGruntWorxExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);

  const checkGruntworxConnection = async () => {
    try {
      const response = await axios.get(GRUNTWORX_INTEGRATION_STATUS);
      if (response.data.success && response.data.data) {
        setConnectionStatus(response.data.data.status);
      } else {
        setConnectionStatus('Inactive');
      }
    } catch (error) {
      console.error('Failed to check GruntWorx connection:', error);
      setConnectionStatus('Inactive');
    }
  };

  // Check connection once when hook is initialized
  useEffect(() => {
    checkGruntworxConnection();
  }, []);

  const resetExport = () => {
    setIsExporting(false);
    setExportStatus(null);
    setPollAttempts(0);
  };

  const pollExportStatus = async (exportId: string) => {
    try {
      const response = await axios.get(`${DMS_FILE_EXPORT}/status`, {
        params: {
          app_key: 'gruntworx',
          export_id: exportId
        }
      });

      const status: ExportStatus = response.data;
      setExportStatus(status);

      setPollAttempts(currentAttempts => {
        const nextAttempt = currentAttempts + 1;

        if (nextAttempt >= MAX_POLL_ATTEMPTS) {
          const timeoutStatus = {
            status: 'failed' as const,
            message: 'Export is taking longer than expected. Please check your GruntWorx dashboard for the current status.',
            success: false
          };
          setExportStatus(timeoutStatus);
          setIsExporting(false);
          onComplete?.(timeoutStatus);
          return nextAttempt;
        }

        switch (status.status) {
          case 'completed':
            setIsExporting(false);
            onComplete?.(status);
            break;

          case 'failed':
            setIsExporting(false);
            onComplete?.(status);
            break;

          case 'processing':
            setTimeout(() => pollExportStatus(exportId), POLL_INTERVAL_PROCESSING);
            break;

          case 'pending':
          default:
            setTimeout(() => pollExportStatus(exportId), POLL_INTERVAL_PENDING);
        }

        return nextAttempt;
      });

    } catch (error: any) {
      setPollAttempts(currentAttempts => {
        const nextAttempt = currentAttempts + 1;

        if (nextAttempt < MAX_POLL_ATTEMPTS) {
          setTimeout(() => pollExportStatus(exportId), POLL_INTERVAL_FAILED);
        } else {
          const errorStatus = {
            status: 'failed' as const,
            message: 'Unable to check export status. Please check your GruntWorx dashboard to verify if the export completed successfully.',
            success: false
          };
          setExportStatus(errorStatus);
          setIsExporting(false);
          onComplete?.(errorStatus);
        }

        return nextAttempt;
      });
    }
  };

  const handleExport = async (documentIds: string[], sourceType: 'Request' | 'Document') => {
    if (documentIds.length === 0) {
      throw new Error('No valid files found for export');
    }

    setIsExporting(true);
    setExportStatus(null);
    setPollAttempts(0);

    try {
      const payload = {
        app_key: 'gruntworx',
        source: sourceType,
        document_ids: documentIds,
        async: 'true'
      };

      const response = await axios.post(DMS_FILE_EXPORT, payload);

      if (response.data.success && response.data.export_id) {
        setExportStatus({
          export_id: response.data.export_id,
          status: 'pending',
          message: 'Export started...'
        });
        pollExportStatus(response.data.export_id);
      } else {
        throw new Error(response.data.message || 'Failed to start export');
      }
    } catch (error: any) {
      console.error('GruntWorx export failed:', error);

      const errorMessage = getExportErrorMessage(error);

      const failedStatus = {
        status: 'failed' as const,
        message: errorMessage,
        success: false
      };
      setExportStatus(failedStatus);
      setIsExporting(false);
      onComplete?.(failedStatus);
    }
  };

  return {
    isExporting,
    exportStatus,
    pollAttempts,
    connectionStatus,
    handleExport,
    resetExport,
    checkGruntworxConnection
  };
}; 